import { SxProps, Theme } from "@mui/material/styles";

const ACCENT_PURPLE = "#7B49C1";
const ACCENT_PURPLE_HOVER = "#6A3DA8";
const ACCENT_PURPLE_LIGHT = "#C4B5FD";

export const landscapeShortMedia =
  "@media (orientation: landscape) and (max-height: 560px)";

export const screenRootStyles: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
  backgroundColor:
    theme.palette.mode === "dark" ? "#05070B" : theme.palette.grey[100],
});

export const topBarStyles: SxProps<Theme> = (theme) => ({
  px: { xs: 1.5, sm: 2 },
  py: { xs: 0.85, sm: 1 },
  borderBottom: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.06)"
      : "rgba(15, 23, 42, 0.06)"
  }`,
  backgroundColor:
    theme.palette.mode === "dark" ? "#05070B" : theme.palette.grey[100],
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  flexShrink: 0,
});

export const topBarTitleStyles: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: { xs: "0.88rem", sm: "0.95rem" },
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
  flex: 1,
};

export const topBarActionsStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 0.5, sm: 1 },
  flexShrink: 0,
};

export const splitLayoutStyles: SxProps<Theme> = (theme) => ({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  overflow: "hidden",
  backgroundColor:
    theme.palette.mode === "dark" ? "#05070B" : theme.palette.grey[100],
  [landscapeShortMedia]: {
    flexDirection: "row",
    alignItems: "stretch",
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
  },
});

export const avatarColumnStyles: SxProps<Theme> = (theme) => ({
  position: "relative",
  flex: { xs: "0 0 auto", sm: "0 0 auto", md: "0 0 42%" },
  width: { xs: "100%", md: "42%" },
  maxWidth: { xs: "100%", md: 480 },
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: { xs: 1, sm: 1.25, md: 1.5 },
  px: { xs: 1.25, sm: 2, md: 2 },
  py: { xs: 1, sm: 1.25, md: 2 },
  backgroundColor:
    theme.palette.mode === "dark" ? "#05070B" : theme.palette.grey[100],
  borderRight: "none",
  [landscapeShortMedia]: {
    flex: "0 0 42%",
    width: "42%",
    maxWidth: 360,
    minHeight: 0,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    px: 1.5,
    py: 0.75,
    gap: 1,
  },
});

export const avatarStageStyles: SxProps<Theme> = (theme) => ({
  position: "relative",
  flex: { xs: "0 0 auto", md: "1 1 auto" },
  minHeight: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: { xs: 0.4, sm: 0.85, md: 1.25 },
  px: { xs: 1.25, sm: 1.5, md: 2 },
  py: { xs: 1.25, sm: 1.5, md: 2 },
  borderRadius: 2,
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "#FFFFFF",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.14)"
      : "rgba(15, 23, 42, 0.14)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 1px 3px rgba(15, 23, 42, 0.05)",
});

export const stagePortraitWrapStyles: SxProps<Theme> = (theme) => ({
  position: "relative",
  // Narrower portrait frame so the photo is not stretched/cropped wide.
  width: {
    xs: "42%",
    sm: "52%",
    md: "86%",
  },
  maxWidth: { xs: 112, sm: 160, md: 300, lg: 320 },
  aspectRatio: "3 / 4",
  flexShrink: 0,
  borderRadius: { xs: "14px", sm: "18px" },
  overflow: "hidden",
  // Shadow on the wrap — overflow:hidden would clip it on the Avatar itself.
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 22px rgba(0,0,0,0.35)"
      : "0 8px 20px rgba(123, 73, 193, 0.18)",
  [landscapeShortMedia]: {
    width: "58%",
    maxWidth: 120,
  },
});

export const stageAvatarStyles: SxProps<Theme> = (theme) => ({
  width: "100%",
  height: "100%",
  borderRadius: { xs: "14px", sm: "18px" },
  // Soft white ring — slightly transparent so the edge looks smoothed.
  border: `2px solid ${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.55)"
      : "rgba(255,255,255,0.88)"
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "inset 0 0 0 1px rgba(255,255,255,0.12)"
      : "inset 0 0 0 1px rgba(255,255,255,0.35), 0 0 0 1px rgba(255,255,255,0.45)",
  "& .MuiAvatar-img": {
    objectFit: "cover",
    objectPosition: "center 18%",
  },
});

export const profileCardStyles: SxProps<Theme> = {
  width: "100%",
  maxWidth: 320,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 0.35,
  px: 1,
};

export const profileCardNameStyles: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: { xs: 13, sm: 15, md: 17 },
  lineHeight: 1.2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
};

export const profileCardStatusStyles: SxProps<Theme> = (theme) => ({
  fontSize: { xs: 11, sm: 12, md: 13 },
  fontWeight: 600,
  color: theme.palette.text.secondary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
});

export const dockedControlsStyles: SxProps<Theme> = {
  position: "relative",
  flexShrink: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 0.75,
  mt: { xs: 0, md: "auto" },
};

export const finishButtonStyles: SxProps<Theme> = {
  textTransform: "none",
  borderRadius: "10px",
  px: { xs: 1.5, sm: 2 },
  minHeight: { xs: 32, sm: 34 },
  fontSize: { xs: 13, sm: 14 },
  fontWeight: 600,
  backgroundColor: ACCENT_PURPLE,
  color: "#FFFFFF !important",
  whiteSpace: "nowrap",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: ACCENT_PURPLE_HOVER,
    color: "#FFFFFF !important",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(123, 73, 193, 0.35)",
    color: "rgba(255, 255, 255, 0.75) !important",
  },
};

export const messagesListStyles: SxProps<Theme> = {
  flex: "1 1 0",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  pr: 0.5,
  pb: 1,
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
};

export const messageStackStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
};

export const userRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  gap: 1,
  width: "100%",
};

export const assistantRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "flex-start",
  gap: 1,
  width: "100%",
};

export const messageAvatarStyles: SxProps<Theme> = {
  width: 32,
  height: 32,
  flexShrink: 0,
};

export const userBubbleStyles: SxProps<Theme> = (theme) => ({
  maxWidth: "calc(85% - 40px)",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(123, 73, 193, 0.28)"
      : "rgba(123, 73, 193, 0.12)",
  border:
    theme.palette.mode === "dark"
      ? "1px solid rgba(196, 181, 253, 0.28)"
      : "none",
  borderRadius: "14px 14px 4px 14px",
  px: 1.5,
  py: 1.25,
  color: theme.palette.mode === "dark" ? "#F3F4F6" : "inherit",
});

export const assistantBubbleStyles: SxProps<Theme> = (theme) => ({
  maxWidth: "calc(85% - 40px)",
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#F3F4F6",
  borderRadius: "14px 14px 14px 4px",
  px: 1.5,
  py: 1.25,
});

export const bubbleLabelStyles: SxProps<Theme> = (theme) => ({
  fontWeight: 700,
  color: theme.palette.mode === "dark" ? ACCENT_PURPLE_LIGHT : ACCENT_PURPLE,
});

export const processingRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mt: 0.5,
};

export const emptyStateStyles: SxProps<Theme> = {
  textAlign: "center",
  mt: 2,
  px: 1,
};

export const loadingContainerStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 280,
  height: "100%",
};

export const errorContainerStyles: SxProps<Theme> = {
  p: 2,
};

export const actionErrorStyles: SxProps<Theme> = {
  mx: 0,
  mb: 0.5,
  borderRadius: "10px",
  flexShrink: 0,
};

export const footerCloseStyles: SxProps<Theme> = {
  p: 0,
};

export const closeButtonStyles: SxProps<Theme> = {
  textTransform: "none",
  borderRadius: "10px",
};

export const errorCloseButtonStyles: SxProps<Theme> = {
  textTransform: "none",
  borderRadius: "10px",
  mt: 2,
};

export const loaderColor = ACCENT_PURPLE;

// @deprecated kept for any external imports
export const headerStyles = topBarStyles;
export const headerAvatarStyles: SxProps<Theme> = {
  width: 40,
  height: 40,
  flexShrink: 0,
};
export const headerTitleBlockStyles: SxProps<Theme> = {
  minWidth: 0,
  flex: 1,
};
export const headerTitleStyles = topBarTitleStyles;
